"use client";

import { useState, useCallback, useMemo, useEffect } from "react";
import type { RowActionRoleFilter } from "@/components/shared/UniversalCRMTable/contexts/SimpleTableRowServicesContext";
import { executeDetailAction } from "@/components/shared/UniversalDetailPage/actions/execute-action";
import type {
  DetailAction,
  DetailActionContext,
  BaseEntity,
} from "@/components/shared/UniversalDetailPage/types/detailPage";

// ============================================================================
// TYPES
// ============================================================================

export interface ActionGroups<T extends BaseEntity> {
  primaryActions: DetailAction<T>[];
  secondaryActions: DetailAction<T>[];
}

export interface UseTableRowActionsReturn<T extends BaseEntity> {
  visibleActions: DetailAction<T>[];
  actionGroups: ActionGroups<T>;
  loadingActions: Set<string>;
  isMobile: boolean;
  handleAction: (action: DetailAction<T>) => Promise<void>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Encapsulates all state and logic for TableRowActions:
 * - Mobile detection
 * - Role-based + visibility filtering
 * - Primary / secondary action grouping
 * - Loading state per action
 * - Action execution
 *
 * `filterActionsByRole` is INJECTED rather than pulled from
 * `useActionFiltering()` here: this hook runs once per row, and the filter is
 * a user-level value (memoized on role + sequences access, no row input). It
 * comes from the ONE table-level subscription in
 * `SimpleTableRowServicesContext`, or from the standalone wrapper outside a
 * table. See that file for why hoisting is behaviour-preserving.
 */
export function useTableRowActions<T extends BaseEntity>(
  item: T,
  actions: DetailAction<T>[],
  actionContext: DetailActionContext<T>,
  maxVisibleActions: number,
  filterActionsByRole: RowActionRoleFilter
): UseTableRowActionsReturn<T> {
  const [loadingActions, setLoadingActions] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice =
        window.innerWidth < 1024 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Filter by role then by item-level visibility
  const visibleActions = useMemo(() => {
    const roleFiltered = filterActionsByRole(actions);
    return roleFiltered.filter((action) => {
      if (action.visible && typeof action.visible === "function") {
        return action.visible(item);
      }
      return true;
    });
  }, [actions, item, filterActionsByRole]);

  // Split into primary (inline) and secondary (dropdown) groups
  const actionGroups = useMemo<ActionGroups<T>>(() => {
    const primary = visibleActions.filter((a) => a.primary);
    const secondary = visibleActions.filter((a) => !a.primary);

    if (primary.length > 0) {
      return {
        primaryActions: primary.slice(0, maxVisibleActions),
        secondaryActions: [...primary.slice(maxVisibleActions), ...secondary],
      };
    }

    if (secondary.length > 0) {
      return {
        primaryActions: secondary.slice(0, maxVisibleActions),
        secondaryActions: secondary.slice(maxVisibleActions),
      };
    }

    return { primaryActions: [], secondaryActions: [] };
  }, [visibleActions, maxVisibleActions]);

  // Execute an action, tracking loading state per action id
  const handleAction = useCallback(
    async (action: DetailAction<T>) => {
      if (action.disabled && typeof action.disabled === "function" && action.disabled(item)) {
        return;
      }

      setLoadingActions((prev) => new Set(prev).add(action.id));
      try {
        await executeDetailAction(action, actionContext);
      } finally {
        setLoadingActions((prev) => {
          const next = new Set(prev);
          next.delete(action.id);
          return next;
        });
      }
    },
    [item, actionContext]
  );

  return { visibleActions, actionGroups, loadingActions, isMobile, handleAction };
}
